
import { Node, parse } from 'acorn';

import { tinyId } from './helpers';


export const DEFAULT_CODE_ID: CodeId = 'code_default';
export const DEFAULT_CODE =
`
function EmptyFunction() {
  
}
`;


export type ArrowId = `arrow_${string}`;
export type FunctionId = `function_${string}`;
export type CodeId = `code_${string}`;

export interface FunctionModel {
  id: FunctionId;
  name: string;
  codeId: CodeId;
  inputs: { name: string, arrowId?: ArrowId, value?: any }[];
  outputs: { name: string, arrowIds: ArrowId[], value?: any }[];
  x?: number;
  y?: number;
}

export interface ArrowModel {
  id: ArrowId;
  from: { id :FunctionId, name: string };
  to: { id :FunctionId, name: string };
}

export interface CodeModel {
  id: CodeId;
  code: string;
  functionIds: FunctionId[];
}

export interface CodeSheet {
  partialArrow?: { id: ArrowId, from: { id: FunctionId, name: string } };
  functionBag: Record<FunctionId, FunctionModel>;
  arrowBag: Record<ArrowId, ArrowModel>;
  codeBag: Record<CodeId, CodeModel>;
}


export function createCodeSheet(): CodeSheet {
  return {
    functionBag: {},
    arrowBag: {},
    codeBag: {
      [DEFAULT_CODE_ID]: {
        id: DEFAULT_CODE_ID,
        code: DEFAULT_CODE,
        functionIds: [],
      },
    },
  }
}

export function createFunction(sheet: CodeSheet) {
  if (!(sheet.codeBag as Record<string, CodeModel>)[DEFAULT_CODE_ID]) {
    throw new Error('MALFORMED CODE SHEET : No "DEFAULT_CODE" in the "codeBag" of the "CodeSheet" !');
  }

  const functionId: FunctionId = `function_${tinyId()}` as FunctionId;

  const newFunction: FunctionModel = {
    id: functionId,
    name: 'EmptyFunction',
    codeId: DEFAULT_CODE_ID,
    inputs: [],
    outputs: [],
  };

  (sheet.codeBag as Record<string, CodeModel>)[DEFAULT_CODE_ID].functionIds.push(functionId);

  (sheet.functionBag as Record<string, FunctionModel>)[functionId] = newFunction;

  return functionId;
}

export function deleteFunction(sheet: CodeSheet, functionId: FunctionId) {
  
  const { codeId, inputs, outputs } = (sheet.functionBag as Record<string, FunctionModel>)[functionId];

  if (!codeId) {
    console.warn(`FUNCTION NOT FOUND: There is no ${functionId} in the current "CodeSheet"`);
    return sheet;
  }

  const arrowIds: ArrowId[] = [
    ...inputs.filter(arrow => !!arrow.arrowId).map(arrow => arrow.arrowId!),
    ...outputs.filter(arrow => !!arrow.arrowIds).map(arrow => arrow.arrowIds!).flat(),
  ];

  arrowIds.forEach(id => deleteArrow(sheet, id));

  const remaining = (sheet.codeBag as Record<string, CodeModel>)[codeId].functionIds.filter(id => id !== functionId);
  
  // TODO if remaining.length === 0 we should delete the code (unless if it's the default)
  (sheet.codeBag as Record<string, CodeModel>)[codeId].functionIds = remaining;

  delete (sheet.functionBag as Record<string, FunctionModel>)[functionId];
}

export function cloneFunction(sheet: CodeSheet, functionId: FunctionId) {
  const functionToClone = (sheet.functionBag as Record<string, FunctionModel>)[functionId];

  if (!functionToClone) {
    throw new Error(`FUNCTION NOT FOUND: There is no ${functionId} in the current "CodeSheet"`);
  }

  const newFunctionId: FunctionId = `function_${tinyId()}` as FunctionId;

  const clone: FunctionModel = {
    id: newFunctionId,
    name: functionToClone.name,
    codeId: functionToClone.codeId,
    inputs: functionToClone.inputs.map(input => ({ name: input.name })),
    outputs: functionToClone.outputs.map(output => ({ name: output.name, arrowIds: [] })),
  };

  (sheet.functionBag as Record<string, FunctionModel>)[newFunctionId] = clone;
  (sheet.codeBag as Record<string, CodeModel>)[functionToClone.codeId].functionIds.push(newFunctionId);

  return newFunctionId;
}

export function setFunctionPosition(sheet: CodeSheet, functionId: FunctionId, x: number, y: number) {

  const functionModel = (sheet.functionBag as Record<string, FunctionModel>)[functionId];

  if (!functionModel) {
    console.warn(`FUNCTION NOT FOUND: There is no ${functionId} in the current "CodeSheet"`);
    return sheet;
  }

  functionModel.x = x;
  functionModel.y = y;

}

export function initArrow(sheet: CodeSheet, fromFunction: FunctionId, outputName: string) {
  const arrowId: ArrowId = `arrow_${tinyId()}` as ArrowId;

  sheet.partialArrow = {
    id: arrowId,
    from: { id: fromFunction, name: outputName },
  };

  return arrowId;
}

export function cancelArrow(sheet: CodeSheet) {
  sheet.partialArrow = undefined;
}

export function linkArrow(sheet: CodeSheet, toFunctionId: FunctionId, inputName: string) {

  const fullArrow: ArrowModel = {
    id: sheet.partialArrow!.id,
    from: sheet.partialArrow!.from,
    to: { id: toFunctionId, name: inputName },
  };

  // TODO THROW IF I/O NAME DOES NOT EXISTS

  const toFunction = (sheet.functionBag as Record<string, FunctionModel>)[fullArrow.to.id];
  const input = toFunction.inputs.find(input => input.name === inputName);
  
  // Check that input is not already taken
  if (!!input?.arrowId) {
    sheet.partialArrow = undefined;
    return;
  }

  const fromFunction = (sheet.functionBag as Record<string, FunctionModel>)[fullArrow.from.id];
  const output = fromFunction.outputs.find(output => output.name === sheet.partialArrow!.from.name);

  // TODO Check that there is no cycle in the graph

  output!.arrowIds.push(sheet.partialArrow!.id);
  input!.arrowId = sheet.partialArrow!.id;
  input!.value = output!.value;

  (sheet.arrowBag as Record<string, ArrowModel>)[sheet.partialArrow!.id] = fullArrow;

  sheet.partialArrow = undefined;

  executeFunction(sheet, toFunctionId);
}

export function deleteArrow(sheet: CodeSheet, arrowId: ArrowId) {

  const arrow = (sheet.arrowBag as Record<string, ArrowModel>)[arrowId];

  if (!arrow) {
    console.warn(`ARROW NOT FOUND: There is no ${arrowId} in the current "CodeSheet" !`);
    return;
  }

  const fromFunction = (sheet.functionBag as Record<string, FunctionModel>)[arrow.from.id];
  const toFunction = (sheet.functionBag as Record<string, FunctionModel>)[arrow.to.id];

  for (let output of fromFunction.outputs) {
    if (output.name === arrow.from.name) {
      const remaining = output.arrowIds.filter(id => id !== arrowId);
      output.arrowIds = remaining;
    }
  }

  for (let input of toFunction.inputs) {
    if (input.arrowId === arrowId) {
      input.arrowId = undefined;
      input.value = undefined;
    }
  }

  delete (sheet.arrowBag as Record<string, ArrowModel>)[arrowId];
}


function parseCode(code: string) {

  // ensure code is valid
  const ast = parse(code, { ecmaVersion: 'latest' });
  const firstFunction = (ast as any).body.find((node: Node) => node.type === 'FunctionDeclaration');

  if (!firstFunction) {
    throw new Error(`INVALID CODE: code MUST contain at least one function at the Program level!`);
  }

  // update all related functions

  const name = firstFunction.id.name;
  const inputs: string[] = firstFunction.params
    .filter((param: Node) => param.type === 'Identifier')
    .map((param: any) => param.name);
  
  let outputs: string[] = [];
  const returnStatement = firstFunction.body.body.find((node: Node) => node.type === 'ReturnStatement');
  if (!!returnStatement) {
    if (returnStatement.argument.type === 'ObjectExpression') {
      outputs = returnStatement.argument.properties.map((property: any) => property.key.name);
    } else {
      outputs = [ 'result' ];
    }
  }

  return { name, inputs, outputs }
}

function parsedToFunction(functionModel: FunctionModel, name: string, inputs: string[], outputs: string[]) {

  functionModel.name = name;

  const newInputs: { name: string, arrowId?: ArrowId, value?: any }[] = inputs.map(input => ({ name: input }));
  for (let newInput of newInputs) {
    for (let oldInput of functionModel.inputs) {
      if (oldInput.name === newInput.name) {
        newInput.arrowId = oldInput.arrowId;
        newInput.value = oldInput.value;
      }
    }
  }
  functionModel.inputs = newInputs;

  const newOutputs: { name: string, arrowIds: ArrowId[], value?: any }[] = outputs.map(output => ({ name: output, arrowIds: [] }));
  for (let newOutput of newOutputs) {
    for (let oldOutput of functionModel.outputs) {
      if (oldOutput.name === newOutput.name) {
        newOutput.arrowIds = oldOutput.arrowIds;
        newOutput.value = oldOutput.value;
      }
    }
  }
  functionModel.outputs = newOutputs;
}


export function createCode(sheet: CodeSheet, functionId: FunctionId, newCode: string) {

  const parentFunction = (sheet.functionBag as Record<string, FunctionModel>)[functionId];

  if (!parentFunction) {
    throw new Error(`FUNCTION NOT FOUND: There is no ${functionId} in the current "CodeSheet" !`);
  }

  const codeId: CodeId = `code_${tinyId()}` as CodeId;

  const newCodeModel: CodeModel = {
    id: codeId,
    code: newCode,
    functionIds: [],
  };

  const { name, inputs, outputs } = parseCode(newCode);

  newCodeModel.functionIds.push(functionId);
  parentFunction.codeId = codeId;
  parsedToFunction(parentFunction, name, inputs, outputs);

  (sheet.codeBag as Record<string, CodeModel>)[codeId] = newCodeModel;

  executeFunction(sheet, functionId);

  return codeId;
}


export function editCode(sheet: CodeSheet, codeId: CodeId, newCode: string) {

  const code = (sheet.codeBag as Record<string, CodeModel>)[codeId];

  if (!code) {
    console.warn(`CODE NOT FOUND: There is no ${codeId} in the current "CodeSheet" !`);
    return;
  }

  if (codeId === DEFAULT_CODE_ID) {
    console.warn('FORBIDDEN EDIT: You cannot edit the default code');
    return;
  }

  const { name, inputs, outputs } = parseCode(newCode);

  // update code
  code.code = newCode;

  code.functionIds.forEach(fnId => {
    const fnModel = (sheet.functionBag as Record<string, FunctionModel>)[fnId];
    if (!fnModel) return;

    parsedToFunction(fnModel, name, inputs, outputs);

    executeFunction(sheet, fnId);
  });
}


export function executeFunction(sheet: CodeSheet, functionId: FunctionId) {

  const functionModel = (sheet.functionBag as Record<string, FunctionModel>)[functionId];
  if (!functionModel) {
    throw new Error(`FUNCTION NOT FOUND: There is no ${functionId} in the current "CodeSheet" !`);
  }

  const codeModel = (sheet.codeBag as Record<string, CodeModel>)[functionModel.codeId];

  const context = `

    ${
      functionModel.inputs.map(input => `
        const input_${input.name} = ${input.value};`
      ).join('')
    }

    const run = ${codeModel.code};

    return run(${
      functionModel.inputs.map(input => `input_${input.name}`).join(', ')
    });
  `;


  try {
    // ! Execution of user defined code
    const result = Function(context)(); // eslint-disable-line no-new-func

    if (!result) return; // if code hasn't returned anything we should stop here

    if (typeof result === 'object') { // ! TODO this will fail is result is an object but was detected as a single output by the code parser

      for (let output of functionModel.outputs) {
        for (let [ key, value] of Object.entries(result)) {
          if (key === output.name) {
            output.value = value;

            // propagate changes
            for (let arrowId of output.arrowIds) {
              const arrow = (sheet.arrowBag as Record<string, ArrowModel>)[arrowId];
              const nextFunction = (sheet.functionBag as Record<string, FunctionModel>)[arrow.to.id];

              const nextInput = nextFunction.inputs.find(input => input.name === arrow.to.name);
              nextInput!.value = result;
              
              executeFunction(sheet, nextFunction.id);
            }
          }
        }
      }
    } else {
      const output = functionModel.outputs.find(output => output.name === 'result');
      output!.value = result;

      // propagate changes
      for (let arrowId of output!.arrowIds) {
        const arrow = (sheet.arrowBag as Record<string, ArrowModel>)[arrowId];
        const nextFunction = (sheet.functionBag as Record<string, FunctionModel>)[arrow.to.id];

        const nextInput = nextFunction.inputs.find(input => input.name === arrow.to.name);
        nextInput!.value = result;
        
        executeFunction(sheet, nextFunction.id);
      }
    }
  } catch (error) {
    throw new Error(`RUNTIME ERROR: ${error}`);
  }
}

